// ─────────────────────────────────────────────────────────────────
// chartStats.ts — Injected chart telemetry
//
// Architecture événementielle :
//   • processedUrls  → ligne grise continue (axe gauche, démarre à 0)
//   • *HitEvent      → 0 | 1 — dot coloré UNIQUEMENT lors d'un nouveau hit
//   • validHitLabels → services détectés (ex: "AWS, SendGrid") pour le tooltip
//
// Clé localStorage partagée entre Command Center et Statistiques :
//   glitch_chart_{startTime}
// ─────────────────────────────────────────────────────────────────

export const CHART_STORAGE_KEY = 'glitch_chart_';

export interface ScanChartPoint {
  time: string;
  processedUrls: number;
  /** 1 si ≥ 1 hit valide nouveaux dans ce poll, 0 sinon */
  validHitEvent: number;
  /** 1 si ≥ 1 hit invalide nouveaux dans ce poll, 0 sinon */
  invalidHitEvent: number;
  /** 1 si ≥ 1 super hit nouveaux dans ce poll, 0 sinon */
  superHitEvent: number;
  speed: number;
  /** Noms des services qui ont eu un nouveau hit valide ex: "AWS, SendGrid" */
  validHitLabels: string;
  [key: string]: number | string;
}

/** Snapshot des compteurs cumulatifs du dernier poll */
export interface ScanHitSnapshot {
  valid:   number;
  invalid: number;
  super:   number;
  /** Compteurs cumulatifs par service (label → count) */
  services: Record<string, number>;
}

export const EMPTY_SNAPSHOT: ScanHitSnapshot = {
  valid: 0, invalid: 0, super: 0, services: {},
};

export interface ScanStatsForChart {
  processedUrls?:    number;
  validHitsCount?:   number;
  invalidHitsCount?: number;
  superHitsCount?:   number;
  startTime?:        string | null;
  // Hit counts par service
  awsHits?:          number;
  sendGridHits?:     number;
  brevoHits?:        number;
  mailgunHits?:      number;
  stripeHits?:       number;
  smtpHits?:         number;
  postmarkHits?:     number;
  twilioHits?:       number;
  sparkpostHits?:    number;
  aliyunHits?:       number;
  tencentHits?:      number;
  mailchimpHits?:    number;
  sendinblueHits?:   number;
  mailJetHits?:      number;
  openAiHits?:       number;
  anthropicHits?:    number;
  googleAiHits?:     number;
  huggingFaceHits?:  number;
  groqHits?:         number;
  cohereHits?:       number;
  mistralHits?:      number;
}

/** Association champ stats → label affiché dans le tooltip */
const SERVICE_HIT_MAP: Array<{ key: keyof ScanStatsForChart; label: string }> = [
  { key: 'awsHits',          label: 'AWS'          },
  { key: 'sendGridHits',     label: 'SendGrid'     },
  { key: 'brevoHits',        label: 'Brevo'        },
  { key: 'mailgunHits',      label: 'MailGun'      },
  { key: 'stripeHits',       label: 'Stripe'       },
  { key: 'smtpHits',         label: 'SMTP'         },
  { key: 'postmarkHits',     label: 'PostMark'     },
  { key: 'twilioHits',       label: 'Twilio'       },
  { key: 'sparkpostHits',    label: 'SparkPost'    },
  { key: 'aliyunHits',       label: 'Aliyun'       },
  { key: 'tencentHits',      label: 'Tencent'      },
  { key: 'mailchimpHits',    label: 'Mailchimp'    },
  { key: 'sendinblueHits',   label: 'Sendinblue'   },
  { key: 'mailJetHits',      label: 'MailJet'      },
  { key: 'openAiHits',       label: 'OpenAI'       },
  { key: 'anthropicHits',    label: 'Anthropic'    },
  { key: 'googleAiHits',     label: 'Google AI'    },
  { key: 'huggingFaceHits',  label: 'HuggingFace'  },
  { key: 'groqHits',         label: 'Groq'         },
  { key: 'cohereHits',       label: 'Cohere'       },
  { key: 'mistralHits',      label: 'Mistral'      },
];

export interface ChartValueScales {
  [key: string]: number;
}

/** Les event-dots sont toujours 0/1 — aucun scale visuel nécessaire */
export function computeChartValueScales(_data: ScanChartPoint[]): ChartValueScales {
  return {};
}

/** @deprecated Utiliser computeChartValueScales */
export function computeHitsChartMultiplier(_data: ScanChartPoint[]): number {
  return 1;
}

export function calculateScanSpeed(stats: ScanStatsForChart): number {
  if (!stats.startTime) return 0;
  try {
    const elapsedSeconds = (Date.now() - new Date(stats.startTime).getTime()) / 1000;
    if (elapsedSeconds <= 0) return 0;
    return Math.round((stats.processedUrls ?? 0) / elapsedSeconds);
  } catch {
    return 0;
  }
}

/**
 * Construit un point de graphique avec calcul delta.
 * Un dot n'apparaît QUE si le compteur a augmenté depuis le poll précédent.
 *
 * Les coordonnées Y des événements (validHitEvent, etc.) sont égales à
 * `processedUrls` au moment du hit — cela place le dot EXACTEMENT sur la
 * ligne des URLs traitées, sur le même axe (gauche).
 * Quand il n'y a pas d'événement, la valeur est 0, donc ColoredDot renvoie null.
 */
export function buildScanChartPoint(
  stats: ScanStatsForChart,
  timeLabel: string,
  prevSnapshot: ScanHitSnapshot = EMPTY_SNAPSHOT,
): ScanChartPoint {
  const processed    = stats.processedUrls ?? 0;
  const validDelta   = Math.max(0, (stats.validHitsCount   ?? 0) - prevSnapshot.valid);
  const invalidDelta = Math.max(0, (stats.invalidHitsCount ?? 0) - prevSnapshot.invalid);
  const superDelta   = Math.max(0, (stats.superHitsCount   ?? 0) - prevSnapshot.super);

  // Déterminer quels services ont produit de nouveaux hits valides
  const detectedLabels: string[] = [];
  if (validDelta > 0) {
    for (const { key, label } of SERVICE_HIT_MAP) {
      const current  = (stats[key] as number | undefined) ?? 0;
      const previous = prevSnapshot.services?.[label] ?? 0;
      if (current > previous) detectedLabels.push(label);
    }
  }

  return {
    time:            timeLabel,
    processedUrls:   processed,
    // Y = processedUrls si événement → dot aligné sur la ligne. Y = 0 → pas de dot.
    validHitEvent:   validDelta   > 0 ? processed : 0,
    invalidHitEvent: invalidDelta > 0 ? processed : 0,
    superHitEvent:   superDelta   > 0 ? processed : 0,
    speed:           calculateScanSpeed(stats),
    validHitLabels:  detectedLabels.join(', '),
    ts:              Date.now(),
  };
}

/** Point zéro placé à l'heure exacte du démarrage du scan */
export function buildZeroChartPoint(startTime: string): ScanChartPoint {
  return {
    time:            formatChartTimeLabel(new Date(startTime)),
    processedUrls:   0,
    validHitEvent:   0,
    invalidHitEvent: 0,
    superHitEvent:   0,
    speed:           0,
    validHitLabels:  '',
    ts:              new Date(startTime).getTime(),
  };
}

/** Extrait le snapshot cumulatif courant depuis les stats du scanner */
export function snapshotFromStats(stats: ScanStatsForChart): ScanHitSnapshot {
  const services: Record<string, number> = {};
  for (const { key, label } of SERVICE_HIT_MAP) {
    services[label] = (stats[key] as number | undefined) ?? 0;
  }
  return {
    valid:   stats.validHitsCount   ?? 0,
    invalid: stats.invalidHitsCount ?? 0,
    super:   stats.superHitsCount   ?? 0,
    services,
  };
}

export function formatChartTimeLabel(date = new Date()): string {
  return `${date.getHours().toString().padStart(2, '0')}:${date
    .getMinutes()
    .toString()
    .padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
}

/** 'dots' = aucune ligne visible, uniquement des petits points colorés */
export type ChartSeriesEmphasis = 'muted' | 'normal' | 'primary' | 'super' | 'dots';

export interface ScanChartKeyConfig {
  key: string;
  name: string;
  color: string;
  yAxisId: 'left' | 'right';
  emphasis: ChartSeriesEmphasis;
}

export const SCAN_CHART_KEYS: ScanChartKeyConfig[] = [
  // Ligne principale grise — URLs traitées (démarre toujours à 0)
  { key: 'processedUrls',   name: 'URLs Traitées', color: '#9ca3af', yAxisId: 'left', emphasis: 'normal' },
  // Dots événementiels — Y = processedUrls au moment du hit → aligné sur la ligne
  { key: 'invalidHitEvent', name: 'Invalides',     color: '#f87171', yAxisId: 'left', emphasis: 'dots'   },
  { key: 'validHitEvent',   name: 'Valides',       color: '#4ade80', yAxisId: 'left', emphasis: 'dots'   },
  { key: 'superHitEvent',   name: 'Super Hits',    color: '#ffe566', yAxisId: 'left', emphasis: 'dots'   },
];

export const HITS_CHART_SCALE_KEYS = ['validHitEvent', 'invalidHitEvent', 'superHitEvent'];
